import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { usersAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.getProfile()
        .then(response => {
          setUser(response.data);
          return usersAPI.getMyPermissions();
        })
        .then(permResponse => {
          setPermissions(permResponse.data.permissions || []);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      try {
        const permResponse = await usersAPI.getMyPermissions();
        setPermissions(permResponse.data.permissions || []);
      } catch (permError) {
        console.error('Yetkiler alınamadı:', permError);
        setPermissions([]);
      }
      
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setPermissions([]);
    setLoading(false);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    // Admin her şeye erişebilir
    if (user.role?.toLowerCase() === 'admin') return true;
    
    // Modül bazlı yetki kontrolü
    return permissions.includes(permission);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      setUser(prevUser => ({ ...prevUser, ...profileData }));
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    updateProfile,
    loading,
    isAuthenticated: !!user,
    permissions,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};